/**
 * RWBR — THE BOOTH: what the booth is allowed to know beyond the race.
 *
 * D-EZ-74. Howard, 21 Aug 2026: *"is there any way to make the in race
 * commentary MORE. More conversation, more descriptive, more colorful, more
 * informative?"*
 *
 * The honest answer was that the booth had been reading about a third of what
 * the engine knows, and none of what the SEASON knows. `callRace` took a
 * `RaceResult` and nothing else, so three things a real broadcast leans on were
 * structurally unavailable:
 *
 *   1. THE SHOP. `Car.card.teamId` is an id. The name that is painted on the
 *      hauler lives on `Team`, which is roster data, not race data — so the
 *      booth could not say "the Ballew & Yeargin car" about a car it was
 *      watching.
 *   2. THE FEUD'S HISTORY. `RaceState.feuds` is a bare list of id PAIRS
 *      (`types.ts` — `{ a, b }` and nothing else). Whether these two have been
 *      at it since Bristol in race 9, or found each other for the first time
 *      forty minutes ago, is the same data structure. `feudHistory.ts` can
 *      answer it, but only from the SEASON's event stream.
 *   3. WHERE WE ARE IN THE YEAR. "Third time this season" is a fact. It is not
 *      in the race.
 *
 * ⚠ EVERY FIELD HERE IS OPTIONAL AND THE BOOTH MUST BE WHOLE WITHOUT ANY OF
 * THEM. An exhibition race has no season and no feud history, and it gets the
 * booth it has always had. That is the deterministic-floor rule applied one
 * level up: the context makes the call RICHER, it never makes it POSSIBLE.
 *
 * D-003 still governs. Nothing in here is a licence to invent — every field is
 * read from a ledger somewhere else and carried in, and a fact built from it is
 * as checkable as a fact built from the turn.
 */

import type { FeudHistory } from '../feudHistory.ts';

/** The shop's name as it would be painted on the hauler, by team id. */
export type TeamNames = ReadonlyMap<string, string>;

export interface BoothContext {
  /**
   * `teamId` → the shop's name. From `field.teams` or `SeasonTeam[]`; the booth
   * never reaches for the roster itself, it is handed the join.
   */
  teams?: TeamNames;
  /**
   * Every feud the season knows about, as `feudHistory.ts` reads them out of
   * the event stream. Keyed however the caller likes — `feudKey` finds the one
   * for a pair.
   */
  feudHistory?: readonly FeudHistory[];
  /** Which race of the season this is, 1-based. */
  raceNumber?: number;
  /** Which season. Only used to say "this season" honestly. */
  seasonNumber?: number;
  /**
   * D-EZ-95 — what the LEAGUE knows going into this race: the points race,
   * the playoff bubble, who won here last time, the defending champion, the
   * milestones and streaks in play, and every rivalry's history. DATA ONLY —
   * the booth does not read it yet (a later phase wires the lines). Built by
   * `history/boothSeason.ts`'s `buildBoothSeasonContext`; every field is a
   * fold over the archived seasons and the live one, never an invention.
   */
  season?: BoothSeasonContext;
  /**
   * D-EZ-111 — the booth's league going into this race: its cast, the people
   * layer and rivalry book cut at the previous race, last week and the
   * analyst's pick (`league.ts`). Absent for an exhibition, which gets the
   * default booth and storylines from the grid alone.
   */
  league?: import('./league.ts').BoothLeague | null;
}

/** One driver in the points table, as the booth would name him. */
export interface BoothStanding {
  driverId: string;
  name: string;
  rank: number;
  points: number;
  wins: number;
}

export interface BoothSeasonContext {
  seasonNumber: number;
  /** The race about to be run, 1-based. */
  raceNumber: number;
  /** 'regular' or the playoff round this race belongs to. */
  round: string;
  /** The points leader going in, and his margin over second. Null before race 1. */
  pointsLeader: (BoothStanding & { marginToSecond: number }) | null;
  /**
   * The playoff picture. In the regular season: who is in on wins, the cut
   * line (the last spot that gets in on points) and the drivers either side of
   * it. In the playoff: who is still alive and where the round's cut falls.
   */
  playoff: {
    phase: 'regular-season' | 'playoff';
    racesToCut: number;
    berths: number;
    inOnWins: string[];
    /** The last driver currently in on points (regular season) / above the round's cut (playoff). */
    cutLine: BoothStanding | null;
    /** Drivers within one race's points of the cut, either side, nearest first. */
    bubble: (BoothStanding & { side: 'in' | 'out'; gap: number })[];
    /** Playoff only: who can still win the title. */
    alive: string[];
  } | null;
  /** Whoever won this named race last time it was run, in any season. */
  defendingRaceWinner: { driverId: string; name: string; seasonNumber: number } | null;
  /** Wins at this physical track, for drivers who have one, most first. */
  trackWinners: { driverId: string; name: string; wins: number }[];
  /** Last season's champion, if the league has had one. */
  defendingChampion: { driverId: string; name: string; seasonNumber: number } | null;
  /** Starts and wins that would be milestones today, first wins chased, droughts. */
  milestonesInPlay: {
    kind: string;
    driverId: string;
    name: string;
    n: number;
    text: string;
  }[];
  /** Streaks running into this race worth a line (win streaks, long top-ten runs). */
  activeStreaks: { driverId: string; name: string; kind: 'wins' | 'top5' | 'top10' | 'finishes'; length: number }[];
  /** Every pair with feud history, most feuds first. */
  rivalries: {
    a: string;
    b: string;
    aName: string;
    bName: string;
    feuds: number;
    races: number;
    seasons: number[];
    live: boolean;
  }[];
  /**
   * D-EZ-108 — the rivalry book going into this race: the hottest pairs still
   * racing (boiling, hot, simmering), with the heat the dice put between them
   * and what they remember. DATA ONLY, for the booth and stakes layers; built
   * by `people/rivalry.ts` off the same archived + live seasons. Optional so
   * older contexts (and tests that build one by hand) still type.
   */
  rivalryHeat?: BoothRivalryHeat[];
}

/** One remembered moment between two drivers, as the booth would cite it. */
export interface BoothRivalryMoment {
  seasonNumber: number;
  /** 0 = over the winter. */
  raceNumber: number;
  raceName: string;
  trackName: string;
  /** The physical track — compare with today's to say "here". */
  venue: string;
  turn: number | null;
  aggressorId: string | null;
  victimId: string | null;
  text: string;
}

export interface BoothRivalryHeat {
  a: string;
  b: string;
  aName: string;
  bName: string;
  heat: number;
  state: 'boiling' | 'hot' | 'simmering';
  trend: 'rising' | 'falling' | 'steady';
  /** Races both started; how often each finished ahead. */
  headToHead: { races: number; aAhead: number; bAhead: number };
  firstSpark: BoothRivalryMoment | null;
  worst: BoothRivalryMoment | null;
  lastIncident: BoothRivalryMoment | null;
  /** Their latest moment at THIS race's track, if they have one. */
  atThisTrack: BoothRivalryMoment | null;
  /** Points between them going in (a − b), when both are in the table. */
  pointsGap: number | null;
  teammates: boolean;
}

export const EMPTY_CONTEXT: BoothContext = {};

/** Order-independent key for a pair of drivers. */
export const feudKey = (a: string, b: string): string => [a, b].sort().join('::');

/** The history for one pair, or null when the season never saw them. */
export function historyFor(
  ctx: BoothContext,
  a: string,
  b: string,
): FeudHistory | null {
  if (!ctx.feudHistory) return null;
  const want = feudKey(a, b);
  return ctx.feudHistory.find((h) => feudKey(h.a, h.b) === want) ?? null;
}

/** The shop that runs this car, or null when nobody told us. */
export function shopOf(ctx: BoothContext, teamId: string | undefined): string | null {
  if (!teamId || !ctx.teams) return null;
  return ctx.teams.get(teamId) ?? null;
}
